import { useEffect, useState } from "react";

import { isSupabaseConfigured, supabase } from "../supabaseClient";

export type Plan = "free" | "pro";
export type SubscriptionStatus = "free" | "active" | "canceled" | "past_due";

export interface SubscriptionState {
  plan: Plan;
  status: SubscriptionStatus;
  loading: boolean;
}

const FREE_STATE: SubscriptionState = {
  plan: "free",
  status: "free",
  loading: false,
};

/**
 * Read the current user's subscription from Supabase.
 * Returns { plan: 'pro', status: 'active' } when the user has an active Pro plan.
 * Falls back to { plan: 'free' } for unauthenticated or unsubscribed users.
 */
export function useSubscription(userId: string | null): SubscriptionState {
  const [state, setState] = useState<SubscriptionState>({
    ...FREE_STATE,
    loading: true,
  });

  useEffect(() => {
    if (!userId || !isSupabaseConfigured || !supabase) {
      setState({ ...FREE_STATE, loading: false });
      return;
    }

    let cancelled = false;

    supabase
      .from("subscriptions")
      .select("plan, status")
      .eq("user_id", userId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) {
          return;
        }
        if (error) {
          setState({ ...FREE_STATE, loading: false });
          return;
        }
        const isPro = data?.status === "active" && data?.plan === "pro";
        setState({
          plan: isPro ? "pro" : "free",
          status: (data?.status as SubscriptionStatus) ?? "free",
          loading: false,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  return state;
}
